import axios from 'axios';
import { appConfig } from 'src/config';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: appConfig.baseUrl,
  withCredentials: true,
  headers: { 'X-App-Source': 'admin2' },
});

// --- Token Refresh Queue ---
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Routes that should NOT trigger the 401 refresh interceptor
const SKIP_INTERCEPTOR_URLS = [
  '/v2/auth/login',
  '/v2/auth/logout',
  '/v2/auth/refresh-token',
];

// --- Request Interceptor ---
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = sessionStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// --- Response Interceptor ---
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    // Check if this is a 401 and the request should be retried
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !SKIP_INTERCEPTOR_URLS.some((url) => originalRequest.url.includes(url))
    ) {
      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        axiosInstance
          .post('/v2/auth/refresh-token')
          .then(({ data }) => {
            const newAccessToken = data.result.accessToken;
            sessionStorage.setItem('accessToken', newAccessToken);

            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            processQueue(null, newAccessToken);
            resolve(axiosInstance(originalRequest));
          })
          .catch((err) => {
            processQueue(err, null);

            // Clear auth state and redirect to login
            sessionStorage.removeItem('accessToken');
            window.location.href = '/auth/login';

            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(
      (error.response && error.response.data) || 'Something went wrong',
    );
  },
);

export default axiosInstance;
